import { applyWorldMechanicsV3, createInitialBoard } from './board';
import { WORLD_IDENTITY_V2, getWorldGoalFamily } from './worldIdentityV2';
import { WORLD_MECHANICS_VERSION } from './worldMechanicsV3';
import { resolveUntilStable } from './resolve';
import { createRng } from './rng';
import { swapWithEvents } from './swap';
import { SIGNS, SIGN_ELEMENT, TILE_KIND } from './type';

export const PROTOCOL_V1_VERSION = '1.0.0';

export const PROTOCOL_V1_WORLD_ORDER = Object.freeze([
  'aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo',
  'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces',
]);

const WORLD_TO_ENGINE_SIGN = Object.freeze({
  aries: 'aries',
  taurus: 'tauro',
  gemini: 'geminis',
  cancer: 'cancer',
  leo: 'leo',
  virgo: 'virgo',
  libra: 'libra',
  scorpio: 'escorpio',
  sagittarius: 'sagitario',
  capricorn: 'capricornio',
  aquarius: 'acuario',
  pisces: 'piscis',
});

const WORLD_ELEMENT = Object.freeze({
  aries: 'fire', leo: 'fire', sagittarius: 'fire',
  taurus: 'earth', virgo: 'earth', capricorn: 'earth',
  gemini: 'air', libra: 'air', aquarius: 'air',
  cancer: 'water', scorpio: 'water', pisces: 'water',
});

function biasForIndex(indexInWorld) {
  if (indexInWorld <= 5) return 0.70;
  if (indexInWorld <= 12) return 0.62;
  return 0.55;
}

function movesForIndex(indexInWorld) {
  if (indexInWorld <= 7) return 20;
  if (indexInWorld <= 19) return 28;
  return 35;
}

export function normalizeProtocolV1LevelConfig(level, options = {}) {
  const absoluteLevel = Math.max(1, Math.min(240, Math.floor(Number(level) || 1)));
  const worldIndex = Math.floor((absoluteLevel - 1) / 20);
  const indexInWorld = ((absoluteLevel - 1) % 20) + 1;
  const worldKey = PROTOCOL_V1_WORLD_ORDER[worldIndex];
  const engineSignKey = WORLD_TO_ENGINE_SIGN[worldKey];
  const config = {
    level: absoluteLevel,
    worldIndex,
    indexInWorld,
    worldKey,
    engineSignKey,
    rows: 8,
    cols: 8,
    signs: Object.freeze([...SIGNS]),
    biasElement: WORLD_ELEMENT[worldKey] || SIGN_ELEMENT[engineSignKey] || null,
    biasStrength: biasForIndex(indexInWorld),
    moveLimit: movesForIndex(indexInWorld),
    cosmicChargeThreshold: 3,
    persistentInteractiveHole: true,
    seedNamespace: `${worldKey}_w${worldIndex}_lvl${absoluteLevel}`,
  };
  if (Number(options?.worldIdentityVersion) === WORLD_IDENTITY_V2.version) {
    config.worldIdentityVersion = WORLD_IDENTITY_V2.version;
    config.goalFamily = getWorldGoalFamily(WORLD_ELEMENT[worldKey]);
  }
  if (Number(options?.worldMechanicsVersion) === WORLD_MECHANICS_VERSION) {
    config.worldMechanicsVersion = WORLD_MECHANICS_VERSION;
  }
  return Object.freeze(config);
}

function tileFromFixture(value, row, col) {
  if (value === null || value?.hole === true) {
    return {
      id: `fixture_hole_${row}_${col}`,
      kind: 'EMPTY',
      key: '+',
      isEmpty: true,
      special: null,
    };
  }
  const key = typeof value === 'string' ? value : value?.key;
  return {
    id: `fixture_${row}_${col}`,
    kind: TILE_KIND.SIGN,
    key,
    element: SIGN_ELEMENT[key] || null,
    rarity: 'normal',
    special: typeof value === 'object' && value ? value.special || null : null,
  };
}

function boardFromFixture(grid, config, meta = {}) {
  const initialMeta = meta && typeof meta === 'object' ? meta : {};
  return {
    rows: grid.length,
    cols: grid[0].length,
    grid: grid.map((line, row) => line.map((value, col) => tileFromFixture(value, row, col))),
    meta: {
      hole: initialMeta.hole || null,
      charge: Number(initialMeta.charge || 0),
      chargeTarget: 3,
      biasElement: config.biasElement,
      biasStrength: config.biasStrength,
      ...(config.worldIdentityVersion === WORLD_IDENTITY_V2.version
        ? { worldIdentityVersion: WORLD_IDENTITY_V2.version, biasWeight: WORLD_IDENTITY_V2.refillBias }
        : {}),
      ...(config.worldMechanicsVersion === WORLD_MECHANICS_VERSION
        ? { worldMechanicsVersion: WORLD_MECHANICS_VERSION, mechanicState: initialMeta.mechanicState || null }
        : {}),
      allowedSigns: [...config.signs],
      level: config.level,
    },
  };
}

export function protocolV1BoardSnapshot(board) {
  return board.grid.map((line) => line.map((tile) => {
    if (!tile || tile.isEmpty === true || String(tile.kind).toLowerCase() === 'empty') {
      return { hole: true };
    }
    return tile.special ? { key: tile.key, special: tile.special } : tile.key;
  }));
}

export function simulateFrontendProtocolV1(rawInput) {
  const version = Number(rawInput.levelConfig?.worldIdentityVersion) === WORLD_IDENTITY_V2.version ? WORLD_IDENTITY_V2.version : 1;
  const mechanicsVersion = Number(rawInput.levelConfig?.worldMechanicsVersion) === WORLD_MECHANICS_VERSION ? WORLD_MECHANICS_VERSION : undefined;
  const config = normalizeProtocolV1LevelConfig(rawInput.levelConfig?.level, { worldIdentityVersion: version, worldMechanicsVersion: mechanicsVersion });
  const rng = createRng(rawInput.seed);
  let board = rawInput.initialBoard
    ? boardFromFixture(rawInput.initialBoard, config, rawInput.initialMeta)
    : createInitialBoard(rng, config.rows, config.cols, version === WORLD_IDENTITY_V2.version
      ? {
        biasElement: config.biasElement,
        biasStrength: config.biasStrength,
        level: config.level,
        worldKey: config.worldKey,
        worldIdentityVersion: version,
      }
      : {
        biasElement: config.biasElement,
        biasStrength: config.biasStrength,
      });
  if (config.worldMechanicsVersion === WORLD_MECHANICS_VERSION && !rawInput.initialBoard) {
    board = applyWorldMechanicsV3(board, createRng(`${rawInput.seed}:mechanics-v3`), {
      worldElement: config.biasElement,
      indexInWorld: config.indexInWorld,
      worldIndex: config.worldIndex,
    });
  }
  const initialBoard = protocolV1BoardSnapshot(board);
  let score = 0;
  let moves = 0;
  let maxCascade = 0;
  let cascades = 0;
  let acceptedActions = 0;
  let rejectedActions = 0;

  for (const action of rawInput.actions || []) {
    if (moves >= config.moveLimit) break;
    const from = { r: action.from.row, c: action.from.col };
    const to = { r: action.to.row, c: action.to.col };
    const swapped = swapWithEvents(board, from, to);
    if (!swapped.valid) {
      rejectedActions += 1;
      continue;
    }
    board = swapped.board;
    const resolved = resolveUntilStable(board, rng, 30, {
      swapFrom: from,
      swapTo: to,
      swapAxis: from.r === to.r ? 'x' : 'y',
    });
    board = resolved.board;
    const depth = (resolved.states || []).filter((state) => (
      state?.phase === 'clear' && Array.isArray(state.clearedCells) && state.clearedCells.length > 0
    )).length;
    score += Number(resolved.scoreDelta || 0);
    moves += 1;
    acceptedActions += 1;
    maxCascade = Math.max(maxCascade, depth);
    cascades += Math.max(0, depth - 1);
  }

  return {
    version: rawInput.version,
    seed: rawInput.seed,
    levelConfig: config,
    initialBoard,
    finalBoard: protocolV1BoardSnapshot(board),
    score,
    moves,
    maxCascade,
    cascades,
    acceptedActions,
    rejectedActions,
    terminalReason: moves >= config.moveLimit ? 'move_limit' : 'actions_exhausted',
    specialStates: board.grid.flat().reduce((acc, tile) => {
      if (tile?.special) acc[tile.special] = (acc[tile.special] || 0) + 1;
      return acc;
    }, {}),
    boardMeta: {
      charge: Number(board.meta?.charge || 0),
      chargeTarget: Number(board.meta?.chargeTarget || 3),
      hole: board.meta?.hole || null,
    },
  };
}

// Explicit alias keeps V1 naming stable while making opt-in V2 evaluation
// discoverable to parity tooling.
export const normalizeProtocolV2LevelConfig = (level) =>
  normalizeProtocolV1LevelConfig(level, { worldIdentityVersion: WORLD_IDENTITY_V2.version });
export const simulateFrontendProtocolV2 = simulateFrontendProtocolV1;
